import React, { useState } from 'react';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SendIcon from '@mui/icons-material/Send';
import { CircularProgress } from '@mui/material';
import { ChatMessage } from '../types/chat';
import { useI18n } from '../hooks/useI18n'; // Import the i18n hook

// API endpoint for feedback
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface MessageFeedbackProps {
  message: ChatMessage;
  sessionId: string | null;
}

type FeedbackState = 'initial' | 'selected' | 'submitting' | 'success' | 'error';

const MessageFeedback: React.FC<MessageFeedbackProps> = ({ message, sessionId }) => {
  const t = useI18n(); // Initialize the translation hook

  const [feedbackState, setFeedbackState] = useState<FeedbackState>('initial');
  const [selectedType, setSelectedType] = useState<'like' | 'dislike' | null>(null);
  const [commentText, setCommentText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Only show feedback for AI messages that aren't currently streaming
  if (message.role !== 'ai' || message.isStreaming) {
    return null;
  }

  // Check if we already have feedback for this message
  const existingFeedback = message.feedback;
  if (existingFeedback && (existingFeedback.type || existingFeedback.feedback_type)) {
    const feedbackType = existingFeedback.type || existingFeedback.feedback_type;
    return (
      <div className="mt-2 flex items-center text-xs text-gray-500">
        {feedbackType === 'like' ? (
          <ThumbUpIcon className="w-3 h-3 mr-1 text-gray-500" fontSize="small" />
        ) : (
          <ThumbDownIcon className="w-3 h-3 mr-1 text-gray-400" fontSize="small" />
        )}
        <span className="text-[10px]">
          {feedbackType === 'like' ? t.get('likedResponse') : t.get('dislikedResponse')}
        </span>
      </div>
    );
  }

  // Handle feedback type selection
  const handleFeedbackClick = (type: 'like' | 'dislike') => {
    if (selectedType === type) {
      // Deselect if clicking the same type
      setSelectedType(null);
      if (feedbackState === 'selected') {
        setFeedbackState('initial');
      }
    } else {
      setSelectedType(type);
      setFeedbackState('selected');
    }
  };

  // Submit feedback to the API
  const submitFeedback = async () => {
    if (!message.id || !sessionId || !selectedType) {
      console.error('Cannot submit feedback: missing required data');
      setErrorMessage('Missing required data for feedback submission');
      setFeedbackState('error');
      return;
    }

    setFeedbackState('submitting');
    setErrorMessage('');

    try {
      const requestBody = {
        chat_log_id: sessionId,
        message_id: message.id,
        feedback_type: selectedType,
        feedback_text: commentText.trim() || '',
      };

      const response = await fetch(`${API_BASE_URL}/chat/message-feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFeedbackState('success');
      } else {
        const errorMsg = data.error || `HTTP ${response.status}: ${response.statusText}`;
        setErrorMessage(errorMsg);
        setFeedbackState('error');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setErrorMessage('Network error occurred. Please try again.');
      setFeedbackState('error');
    }
  };

  // Reset to initial state
  const handleReset = () => {
    setSelectedType(null);
    setCommentText('');
    setErrorMessage('');
    setFeedbackState('initial');
  };

  // Success state
  if (feedbackState === 'success') {
    return (
      <div className="mt-1 flex items-center text-[10px] text-gray-500">
        {selectedType === 'like' ? (
          <ThumbUpIcon className="w-3 h-3 mr-1 text-gray-500" fontSize="small" />
        ) : (
          <ThumbDownIcon className="w-3 h-3 mr-1 text-gray-500" fontSize="small" />
        )}
        <span>{t.get('thanksForFeedback')}</span>
      </div>
    );
  }

  // Error state
  if (feedbackState === 'error') {
    return (
      <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
        <div className="text-[10px] text-red-600 mb-1">
          {t.get('failedToSubmit')}: {errorMessage}
        </div>
        <div className="flex justify-end gap-1">
          <button
            className="text-[10px] text-gray-600 hover:text-gray-800 px-1 py-0.5"
            onClick={handleReset}
          >
            {t.get('cancel')}
          </button>
          <button
            className="text-[10px] text-blue-600 hover:text-gray-800 px-1 py-0.5 underline"
            onClick={() => setFeedbackState('selected')}
          >
            {t.get('tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Main feedback interface
  return (
    <div className="mt-1">
      {/* All elements in one line */}
      <div className="flex flex-wrap items-center gap-1.5 mt-1">
        {/* Thumbs Up/Down */}
        <button
          className="p-0.5 hover:bg-gray-100 rounded transition-colors duration-200 flex-shrink-0"
          onClick={() => handleFeedbackClick('like')}
          aria-label="Like this response"
        >
          {selectedType === 'like' ? (
            <ThumbUpIcon className="w-3 h-3 text-gray-500" fontSize="small" />
          ) : (
            <ThumbUpOutlinedIcon className="w-3 h-3 text-gray-500" fontSize="small" />
          )}
        </button>

        <button
          className="p-0.5 hover:bg-gray-100 rounded transition-colors duration-200 flex-shrink-0"
          onClick={() => handleFeedbackClick('dislike')}
          aria-label="Dislike this response"
        >
          {selectedType === 'dislike' ? (
            <ThumbDownIcon className="w-3 h-3 text-gray-500" fontSize="small" />
          ) : (
            <ThumbDownOutlinedIcon className="w-3 h-3 text-gray-500" fontSize="small" />
          )}
        </button>

        {/* Comment Input and Submit - shows when feedback is selected */}
        {(feedbackState === 'selected' || feedbackState === 'submitting') && (
          <>
            <input
              type="text"
              className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded-full text-[10px] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-transparent"
              placeholder={t.get('provideFeedback')}
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              disabled={feedbackState === 'submitting'}
            />

            <button
              onClick={submitFeedback}
              disabled={feedbackState === 'submitting' || !selectedType}
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                feedbackState === 'submitting'
                  ? 'bg-gray-300 cursor-not-allowed'
                  : '!bg-[#62004d] text-white hover:bg-[#50003d]'
              }`}
              aria-label={t.get('submitFeedback')}
            >
              {feedbackState === 'submitting' ? (
                <CircularProgress size={14} className="text-gray-500" />
              ) : (
                <SendIcon style={{ fontSize: 14 }} className="ml-0.5" />
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default MessageFeedback;
